import { useState } from "react";
import { AnalyticsPage } from "@/components/analytics/AnalyticsPage";
import { Icon } from "@/components/analytics/Icon";
import { OriginTag } from "@/components/analytics/pieces";
import { type Avatar, type Session } from "@/lib/analytics/demo-data";
import { useAnalyticsData } from "@/lib/analytics/range";

type SortKey = "learner" | "avatar" | "datetime" | "duration" | "score" | "pass";

function sortVal(
  s: Session,
  key: SortKey,
  avatarById: (id: string) => Avatar | undefined,
): string | number | null {
  const a = avatarById(s.avatar);
  switch (key) {
    case "learner":
      return s.learnerId ? s.learner : "zzz_anonymous";
    case "avatar":
      return a?.name ?? "";
    case "datetime":
      return s.ts;
    case "duration":
      return s.min;
    case "score":
      return s.score;
    case "pass":
      return s.pass === null ? null : s.pass ? 1 : 0;
    default:
      return null;
  }
}

export function AnalyticsSessionsPage() {
  const { sessions, avatarById, downloadTranscript } = useAnalyticsData();
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "datetime",
    dir: "desc",
  });

  const rows = [...sessions].sort((a, b) => {
    const mul = sort.dir === "asc" ? 1 : -1;
    const va = sortVal(a, sort.key, avatarById);
    const vb = sortVal(b, sort.key, avatarById);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === "string" && typeof vb === "string") return va.localeCompare(vb) * mul;
    return ((va as number) - (vb as number)) * mul;
  });

  const toggle = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  const Th = ({ k, label, num }: { k: SortKey; label: string; num?: boolean }) => (
    <th className={`sortable${num ? " num" : ""}`} onClick={() => toggle(k)}>
      {label}
      {sort.key !== k ? (
        <Icon name="unfold_more" className="sort-ic idle" />
      ) : (
        <Icon name={sort.dir === "asc" ? "arrow_upward" : "arrow_downward"} className="sort-ic on" />
      )}
    </th>
  );

  return (
    <AnalyticsPage
      title="Sessions log · Live Avatars Analytics"
      description="Every live avatar session: learner, channel of origin, duration, score, pass status and downloadable transcript."
    >
      <div className="page-head">
        <div>
          <h1>Sessions</h1>
          <p>
            Every session is one learner, one avatar, one sitting. Sort by any column. Score and pass
            are N/A for non-scorable (conversational / roleplay) avatars.
          </p>
        </div>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Learner ID</th>
              <Th k="learner" label="Learner" />
              <Th k="avatar" label="Live avatar" />
              <Th k="datetime" label="Date & time" />
              <th>Origin</th>
              <Th k="duration" label="Duration" num />
              <Th k="score" label="Score" num />
              <Th k="pass" label="Pass" num />
              <th className="num">Transcript</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const a = avatarById(s.avatar)!;
              const scored = a.is_scorable && s.score !== null;
              return (
                <tr key={s.id}>
                  <td>
                    {s.learnerId ? (
                      <span className="lid">{s.learnerId}</span>
                    ) : (
                      <span className="lid none">—</span>
                    )}
                  </td>
                  <td className="lname">
                    {s.learnerId ? (
                      <span className="nm inline-flex items-center gap-1.5">
                        {s.learnerThumb && (
                          <img
                            src={s.learnerThumb}
                            alt=""
                            loading="lazy"
                            className="h-5 w-5 shrink-0 rounded-full object-cover"
                          />
                        )}
                        {s.learner}
                      </span>
                    ) : (
                      <span className="anon">Anonymous</span>
                    )}
                  </td>
                  <td>{a.name}</td>
                  <td className="muted-cell" style={{ whiteSpace: "nowrap" }}>
                    {s.when}
                  </td>
                  <td>
                    <OriginTag origin={s.origin} />
                  </td>
                  <td className="num">{s.min} min</td>
                  <td className="num">
                    {scored ? `${s.score}%` : <span className="pill na">N/A</span>}
                  </td>
                  <td className="num">
                    {scored ? (
                      s.pass ? (
                        <span className="pill pass">Yes</span>
                      ) : (
                        <span className="pill fail">No</span>
                      )
                    ) : (
                      <span className="pill na">N/A</span>
                    )}
                  </td>
                  <td className="num">
                    <button
                      className="dl-btn"
                      title="Download session transcript"
                      aria-label="Download session transcript"
                      onClick={() => downloadTranscript(s)}
                    >
                      <Icon name="download" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AnalyticsPage>
  );
}